import { useState, useEffect } from "react";
import { BarChart3, AlertTriangle, ChevronDown } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

/**
 * Einstand & Kalkulation (Feb-2026, User-Wunsch):
 *  - Einkaufspreis (EK), Besteuerung (Netto/MwSt bei Regelbesteuerung),
 *    interne Werkstattkosten (automatisch aus verknüpften Werkstattaufträgen),
 *    Fremdrechnungen (manuell), Einstand-Summary, Roh-Marge mit Marge-Ampel
 *    (grün/gelb/rot) + Wunschmarge-Warnung, Lagerort.
 *  - Einklappbar, startet IMMER eingeklappt. Steht unter dem KI-Preisvorschlag.
 */
export default function EinstandCalcBlock({ form, update, listingId }) {
    const [open, setOpen] = useState(false);
    const [costSummary, setCostSummary] = useState(null);

    useEffect(() => {
        if (!listingId) return;
        let active = true;
        api.get(`/listings/${listingId}/dispo/cost-summary`)
            .then((r) => { if (active) setCostSummary(r.data); })
            .catch(() => { if (active) setCostSummary(null); });
        return () => { active = false; };
    }, [listingId]);

    const extItems = Array.isArray(form.external_cost_items) ? form.external_cost_items : [];
    const setExt = (next) => update("external_cost_items", next);
    const addExt = () => setExt([...extItems, { label: "", amount: "" }]);
    const updExt = (i, key, val) => setExt(extItems.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));
    const delExt = (i) => setExt(extItems.filter((_, idx) => idx !== i));
    const num = (v) => { const n = Number(v); return isFinite(n) ? n : 0; };
    const ekVal = num(form.purchase_price);
    const workshopTotal = num(costSummary?.workshop_total_gross);
    const bonisTotal = num(costSummary?.bonis_total);
    const bonisOpen = num(costSummary?.bonis_open);
    const extTotal = extItems.reduce((s, x) => s + num(x.amount), 0);
    // Jul-2026 (User-Wunsch) — Zustand & Schadensermittlung fließt in die Kalkulation
    // ein: erfasste Abzüge (GW-Preisliste) erhöhen den Einstand (erwartete Instandsetzung).
    const ca = form.condition_assessment || null;
    const conditionDeduction = num(ca?.deduction_total);
    const conditionDone = !!ca?.complete;
    const einstand = ekVal + workshopTotal + extTotal + conditionDeduction - bonisTotal;
    const provision = num(form.referral_commission_eur);
    const vkB2C = num(form.price);
    const isRegel = form.vat_included !== false;
    const vatRate = 0.19;
    const nettoB2C = vkB2C > 0 ? vkB2C / (1 + vatRate) : 0;
    const mwstB2C = vkB2C - nettoB2C;
    const fmtEur = (n) => `${Math.round(n).toLocaleString("de-DE")} €`;
    const rohMarge = vkB2C - einstand - provision;
    const margePct = vkB2C > 0 ? (rohMarge / vkB2C) * 100 : 0;
    // Feb-2026 (User-Wunsch P1) — zweite Margen-Zeile inkl. noch erwarteter Boni.
    const rohMargeMitBoni = rohMarge + bonisOpen;
    const margeMitBoniPct = vkB2C > 0 ? (rohMargeMitBoni / vkB2C) * 100 : 0;
    const targetMargin = num(form.target_margin_eur);
    const belowTarget = targetMargin > 0 && rohMarge < targetMargin;
    const ampel = vkB2C <= 0 ? null : (rohMarge < 0 || belowTarget ? "rot" : margePct < 8 ? "gelb" : "gruen");
    const ampelMap = {
        rot: { dot: "bg-red-500", text: "text-red-600", label: "Kritisch" },
        gelb: { dot: "bg-amber-500", text: "text-amber-600", label: "Dünn" },
        gruen: { dot: "bg-emerald-500", text: "text-emerald-700", label: "Gesund" },
    };

    return (
        <div className="border-2 border-gray-200 rounded-sm bg-gray-50/60 overflow-hidden" data-testid="calc-cost-block">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-100 transition-colors"
                aria-expanded={open}
                data-testid="calc-cost-toggle"
            >
                <BarChart3 className="w-4 h-4 text-[#111827]" />
                <span className="font-bold text-sm uppercase tracking-wider text-[#111827] flex-1">Einstand &amp; Kalkulation</span>
                {ampel && vkB2C > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${ampelMap[ampel].dot}`} data-testid="marge-ampel-dot-summary" />
                        <span className={ampelMap[ampel].text}>{fmtEur(rohMarge)}</span>
                    </span>
                )}
                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div className="px-4 pb-4" data-testid="calc-cost-content">
                    {/* Jul-2026 (User-Wunsch) — gut sichtbarer Hinweis, wenn die Zustand &
                        Schadensermittlung noch nicht vollständig durchgeführt wurde. */}
                    {!conditionDone && (
                        <div className="mb-3 flex items-center justify-between gap-2 rounded-sm border-2 border-amber-400 bg-amber-50 px-3 py-2.5" data-testid="condition-missing-warning">
                            <div className="flex items-start gap-2 text-[12px] text-amber-900">
                                <AlertTriangle className="w-4 h-4 mt-px shrink-0 text-amber-600" />
                                <span><b>Zustand &amp; Schadensermittlung noch nicht durchgeführt.</b> Unterlagen, Schlüssel und Technik/Optik sind Pflicht — die Abzüge fließen in diese Kalkulation ein.</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    window.dispatchEvent(new CustomEvent("open-condition-assessment"));
                                    document.querySelector('[data-testid="listing-schadensermittlung"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
                                }}
                                className="shrink-0 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm bg-amber-500 hover:bg-amber-600 text-white"
                                data-testid="condition-missing-link"
                            >
                                Jetzt durchführen →
                            </button>
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Einkaufspreis (EK) */}
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Einkaufspreis (EK)</Label>
                            <div className="relative mt-1.5">
                                <Input
                                    type="number"
                                    min="0"
                                    value={form.purchase_price ?? ""}
                                    onChange={(e) => update("purchase_price", e.target.value)}
                                    placeholder="z. B. 8500"
                                    className="rounded-sm font-mono-num pr-8"
                                    data-testid="form-purchase-price"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">€</span>
                            </div>
                        </div>
                        {/* Besteuerung Netto / MwSt (nur Regelbesteuerung) */}
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Besteuerung (VK B2C)</Label>
                            {isRegel ? (
                                <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="tax-netto-mwst">
                                    <div className="bg-white border border-gray-200 rounded-sm px-2.5 py-2">
                                        <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Netto</div>
                                        <div className="font-mono-num text-sm font-bold" data-testid="tax-netto">{vkB2C > 0 ? fmtEur(nettoB2C) : "—"}</div>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded-sm px-2.5 py-2">
                                        <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">MwSt 19 %</div>
                                        <div className="font-mono-num text-sm font-bold" data-testid="tax-mwst">{vkB2C > 0 ? fmtEur(mwstB2C) : "—"}</div>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-1.5 bg-white border border-gray-200 rounded-sm px-2.5 py-2 text-[12px] text-gray-600" data-testid="tax-differenz-hint">
                                    Differenzbesteuert (§ 25a UStG) — keine ausweisbare MwSt.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Werkstattkosten (intern, automatisch) */}
                    <div className="mt-4">
                        <div className="flex items-center justify-between">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Interne Werkstattkosten (automatisch)</Label>
                            <span className="font-mono-num text-sm font-bold" data-testid="workshop-cost-total">{fmtEur(workshopTotal)}</span>
                        </div>
                        {!listingId ? (
                            <p className="mt-1 text-[11px] text-gray-400" data-testid="workshop-cost-na">Verfügbar nach dem Speichern — zieht Kosten aus verknüpften Werkstattaufträgen.</p>
                        ) : (costSummary?.workshop_orders || []).length > 0 ? (
                            <ul className="mt-1.5 space-y-1" data-testid="workshop-cost-list">
                                {costSummary.workshop_orders.map((o) => (
                                    <li key={o.id} className="flex items-center justify-between gap-2 bg-white border border-gray-200 rounded-sm px-2.5 py-1.5 text-[12px]">
                                        <span className="font-mono-num text-gray-700">{o.order_number} · {o.status}</span>
                                        <span className="font-mono-num font-bold">{fmtEur(num(o.total_gross))}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="mt-1 text-[11px] text-gray-400" data-testid="workshop-cost-empty">Keine verknüpften Werkstattaufträge für dieses Fahrzeug.</p>
                        )}
                    </div>

                    {/* Fremdrechnungen (manuell nachtragen) */}
                    <div className="mt-4">
                        <div className="flex items-center justify-between">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Fremdrechnungen (manuell)</Label>
                            <button
                                type="button"
                                onClick={addExt}
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-[#111827] hover:bg-black text-white"
                                data-testid="add-external-cost-btn"
                            >
                                + Position
                            </button>
                        </div>
                        {extItems.length > 0 && (
                            <ul className="mt-1.5 space-y-1.5" data-testid="external-cost-list">
                                {extItems.map((x, i) => (
                                    <li key={i} className="flex items-center gap-2" data-testid={`external-cost-row-${i}`}>
                                        <Input
                                            value={x.label ?? ""}
                                            onChange={(e) => updExt(i, "label", e.target.value)}
                                            placeholder="Bezeichnung (z. B. Reifen extern)"
                                            className="rounded-sm text-sm flex-1"
                                            data-testid={`external-cost-label-${i}`}
                                        />
                                        <div className="relative w-32 shrink-0">
                                            <Input
                                                type="number"
                                                min="0"
                                                value={x.amount ?? ""}
                                                onChange={(e) => updExt(i, "amount", e.target.value)}
                                                placeholder="0"
                                                className="rounded-sm font-mono-num pr-6 text-sm"
                                                data-testid={`external-cost-amount-${i}`}
                                            />
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs pointer-events-none">€</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => delExt(i)}
                                            className="text-gray-400 hover:text-red-600 px-1"
                                            data-testid={`external-cost-remove-${i}`}
                                            aria-label="Position entfernen"
                                        >
                                            ✕
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Einstand-Summary + Lagerort */}
                    <div className="mt-4 pt-3 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="einstand-summary">
                        <div className="bg-white border border-gray-200 rounded-sm p-3 space-y-1 text-[12px]">
                            <div className="flex justify-between"><span className="text-gray-500">Einkaufspreis</span><span className="font-mono-num">{fmtEur(ekVal)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">+ Werkstatt (intern)</span><span className="font-mono-num">{fmtEur(workshopTotal)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">+ Fremdrechnungen</span><span className="font-mono-num">{fmtEur(extTotal)}</span></div>
                            {conditionDeduction > 0 && (
                                <div className="flex justify-between text-amber-700" data-testid="einstand-condition-row"><span>+ Zustand/Schäden (GW-Preisliste)</span><span className="font-mono-num">{fmtEur(conditionDeduction)}</span></div>
                            )}
                            {bonisTotal > 0 && (
                                <div className="flex justify-between text-emerald-700" data-testid="einstand-bonis-row"><span>− Bonis (erhalten)</span><span className="font-mono-num">−{fmtEur(bonisTotal)}</span></div>
                            )}
                            <div className="flex justify-between border-t border-gray-100 pt-1 mt-1 font-bold"><span>= Einstand</span><span className="font-mono-num" data-testid="einstand-total">{fmtEur(einstand)}</span></div>
                            {provision > 0 && <div className="flex justify-between text-violet-700"><span>Vermittler-Provision</span><span className="font-mono-num">{fmtEur(provision)}</span></div>}
                            {bonisOpen > 0 && (
                                <div className="flex justify-between items-center text-amber-700 border-t border-dashed border-amber-200 pt-1 mt-1" data-testid="einstand-bonis-open-row">
                                    <span className="inline-flex items-center gap-1">Zu erwartende Boni (ausstehend)</span>
                                    <span className="font-mono-num font-bold">+{fmtEur(bonisOpen)}</span>
                                </div>
                            )}
                            {vkB2C > 0 && (
                                <div className="flex justify-between items-center border-t border-gray-100 pt-1 mt-1 font-bold" data-testid="roh-marge-row">
                                    <span className="inline-flex items-center gap-1.5">
                                        {ampel && <span className={`inline-block w-2.5 h-2.5 rounded-full ${ampelMap[ampel].dot}`} data-testid="marge-ampel-dot" title={ampelMap[ampel].label} />}
                                        Roh-Marge
                                    </span>
                                    <span className={`font-mono-num ${ampel ? ampelMap[ampel].text : ""}`} data-testid="roh-marge-value">{fmtEur(rohMarge)}{vkB2C > 0 && <span className="text-[10px] font-normal ml-1">({margePct.toFixed(1)} %)</span>}</span>
                                </div>
                            )}
                            {vkB2C > 0 && bonisOpen > 0 && (
                                <div className="flex justify-between items-center font-bold text-emerald-700" data-testid="roh-marge-mit-boni-row">
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                        Roh-Marge inkl. erwarteter Boni
                                    </span>
                                    <span className="font-mono-num" data-testid="roh-marge-mit-boni-value">{fmtEur(rohMargeMitBoni)}<span className="text-[10px] font-normal ml-1">({margeMitBoniPct.toFixed(1)} %)</span></span>
                                </div>
                            )}
                            {belowTarget && (
                                <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-sm px-2 py-1.5" data-testid="marge-warning">
                                    <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                                    <span>Roh-Marge liegt unter der Wunschmarge ({fmtEur(targetMargin)}) — Verkauf nur nach Prüfung.</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center border-t border-gray-100 pt-1.5 mt-1.5">
                                <span className="text-gray-500">Wunschmarge (min.)</span>
                                <div className="relative w-28">
                                    <Input
                                        type="number"
                                        min="0"
                                        value={form.target_margin_eur ?? ""}
                                        onChange={(e) => update("target_margin_eur", e.target.value)}
                                        placeholder="0"
                                        className="h-8 rounded-sm font-mono-num pr-6 text-xs text-right"
                                        data-testid="form-target-margin"
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs pointer-events-none">€</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-sm p-3">
                            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Lagerort / Standort</div>
                            <div className="text-sm mt-0.5" data-testid="calc-location">{form.location || form.stand_platz || "—"}</div>
                            {form.stand_platz && <div className="text-[11px] text-gray-500 mt-0.5">Stand-Platz: {form.stand_platz}</div>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
